import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, Shield, Lock, Clock, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { chatbotAPI } from '@/utils/api';

/**
 * ChatBot for A/B test variant C
 *
 * Psychological approach:
 * - Disarm fear → Create control → Allow engagement
 * - Never judge, never use "dette" at the beginning
 * - Remind anonymity regularly
 */

// Chatbot script with 15 messages
const CHATBOT_SCRIPT = [
  {
    id: 1,
    type: 'message',
    content: "Bonjour.\nVous avez recu un courrier de recouvrement et vous ne savez pas si vous devez payer ?\nJe vais vous aider a verifier ca en quelques questions.",
    buttons: [{ label: 'Continuer', value: 'continue' }],
  },
  {
    id: 2,
    type: 'message',
    content: "68% des dossiers que nous analysons presentent des irregularites : prescription depassee, procedure non conforme, ou montants injustifies.\nVoyons si c'est votre cas.",
    buttons: [{ label: "D'accord", value: 'ok' }],
  },
  {
    id: 3,
    type: 'question',
    questionId: 'timeframe',
    content: "La demande que vous avez recue concerne-t-elle quelque chose qui date de :",
    options: [
      { label: "moins d'1 an", value: 'less_1_year' },
      { label: '1 a 3 ans', value: '1_to_3_years' },
      { label: 'plus de 3 ans', value: 'more_3_years' },
      { label: 'je ne sais pas', value: 'unknown' },
    ],
  },
  {
    id: 4,
    type: 'message',
    content: "Merci. C'est une information suffisante pour commencer.",
    buttons: [{ label: 'Continuer', value: 'continue' }],
  },
  {
    id: 5,
    type: 'question',
    questionId: 'pressure_type',
    content: "Avez-vous recu recemment :",
    options: [
      { label: 'un courrier simple', value: 'simple_mail' },
      { label: 'une mise en demeure', value: 'formal_notice' },
      { label: 'un huissier', value: 'bailiff' },
      { label: 'je ne sais pas', value: 'unknown' },
    ],
  },
  {
    id: 6,
    type: 'message',
    content: "Tres bien.\nContrairement a ce que beaucoup pensent, tous les courriers n'ont pas la meme valeur juridique.",
    buttons: [{ label: 'Continuer', value: 'continue' }],
  },
  {
    id: 7,
    type: 'question',
    questionId: 'judgment',
    content: "Savez-vous si un jugement a deja ete rendu contre vous ?",
    options: [
      { label: 'oui', value: 'yes' },
      { label: 'non', value: 'no' },
      { label: 'je ne sais pas', value: 'unknown' },
    ],
  },
  {
    id: 8,
    type: 'message',
    content: "Vous faites bien de verifier.\nBeaucoup de personnes decouvrent des elements importants a ce stade.",
    buttons: [{ label: 'Continuer', value: 'continue' }],
  },
  {
    id: 9,
    type: 'message',
    content: "D'apres ce que vous indiquez, votre situation merite une verification juridique serieuse.\nDans de nombreux cas similaires, des irregularites sont constatees.",
    buttons: [{ label: 'Continuer', value: 'continue' }],
  },
  {
    id: 10,
    type: 'choice',
    questionId: 'continue_analysis',
    content: "Si vous le souhaitez, je peux approfondir l'analyse.\nCela reste confidentiel et sans engagement.",
    options: [
      { label: "Continuer l'analyse", value: 'continue' },
      { label: "S'arreter ici", value: 'stop' },
    ],
  },
  {
    id: 11,
    type: 'message',
    content: "Pour aller plus loin, j'aurai besoin de quelques elements supplementaires.\nVous pouvez repondre partiellement ou arreter a tout moment.",
    buttons: [{ label: 'Continuer', value: 'continue' }],
  },
  {
    id: 12,
    type: 'message',
    content: "A ce stade, certaines personnes choisissent d'indiquer :\n- le type de creancier\n- l'annee approximative\n- ou le type de document recu\n\nAucun montant n'est obligatoire.",
    buttons: [{ label: 'Continuer', value: 'continue' }],
  },
  {
    id: 13,
    type: 'question',
    questionId: 'creditor_type',
    content: "Quel type de creancier est concerne ?",
    options: [
      { label: 'banque', value: 'bank' },
      { label: 'organisme de credit', value: 'credit_org' },
      { label: 'operateur / fournisseur', value: 'provider' },
      { label: 'autre', value: 'other' },
      { label: 'je prefere ne pas repondre', value: 'no_answer' },
    ],
  },
  {
    id: 14,
    type: 'message',
    content: "Merci.\nDans beaucoup de cas, cette information permet deja d'identifier des leviers juridiques.",
    buttons: [{ label: 'Continuer', value: 'continue' }],
  },
  {
    id: 15,
    type: 'final',
    questionId: 'start_analysis',
    content: "Souhaitez-vous obtenir une analyse juridique personnalisee, basee sur le droit francais ?",
    options: [
      { label: 'Oui, continuer', value: 'yes' },
      { label: 'Non, pas maintenant', value: 'no' },
    ],
  },
];

// Generate unique session ID for chatbot
const getChatbotSessionId = () => {
  let sessionId = sessionStorage.getItem('chatbot_session_id');
  if (!sessionId) {
    sessionId = `chatbot_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('chatbot_session_id', sessionId);
  }
  return sessionId;
};

const ChatBotVariantC = ({ onComplete, onStop }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [messages, setMessages] = useState([]);
  const [answers, setAnswers] = useState({});
  const [isTyping, setIsTyping] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const messagesEndRef = useRef(null);
  const sessionId = useRef(getChatbotSessionId());

  // Track chatbot step in GA4
  const trackChatbotStep = (stepId, action, value = null) => {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'chatbot_step', {
        event_category: 'Chatbot',
        step_id: stepId,
        action: action,
        value: value,
        session_id: sessionId.current,
      });
    }
  };

  // Save conversation to backend
  const saveConversation = async (newAnswers) => {
    try {
      await chatbotAPI.save({
        session_id: sessionId.current,
        answers: newAnswers,
        current_step: currentStep + 1,
        completed: false,
      });
    } catch (error) {
      console.debug('Chatbot save error:', error);
    }
  };

  // Mark conversation as completed
  const completeConversation = async (outcome) => {
    try {
      await chatbotAPI.complete(sessionId.current, outcome);
    } catch (error) {
      console.debug('Chatbot complete error:', error);
    }
  };

  // Scroll to bottom when new messages appear
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Display initial message
  useEffect(() => {
    displayMessage(0);
    trackChatbotStep(1, 'start');
  }, []);

  const displayMessage = (stepIndex) => {
    if (stepIndex >= CHATBOT_SCRIPT.length) return;

    const step = CHATBOT_SCRIPT[stepIndex];
    setIsTyping(true);
    setShowOptions(false);

    // Simulate typing delay
    setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        {
          type: 'bot',
          content: step.content,
          step: step,
        },
      ]);
      setIsTyping(false);

      // Show options after a short delay
      setTimeout(() => {
        setShowOptions(true);
      }, 300);
    }, 800 + Math.random() * 400);
  };

  const handleOptionClick = async (step, option) => {
    // Add user response to messages
    setMessages((prev) => [
      ...prev,
      {
        type: 'user',
        content: option.label,
      },
    ]);

    // Track the response
    trackChatbotStep(step.id, 'answer', option.value);

    // Save answer if it's a question
    if (step.questionId) {
      const newAnswers = { ...answers, [step.questionId]: option.value };
      setAnswers(newAnswers);
      await saveConversation(newAnswers);
    }

    // Handle special cases
    if (step.type === 'choice' && option.value === 'stop') {
      trackChatbotStep(step.id, 'stopped');
      await completeConversation('stopped');
      onStop?.();
      return;
    }

    if (step.type === 'final') {
      if (option.value === 'yes') {
        trackChatbotStep(step.id, 'completed');
        await completeConversation('completed');
        onComplete?.(answers);
      } else {
        trackChatbotStep(step.id, 'declined');
        await completeConversation('declined');
        onStop?.();
      }
      return;
    }

    // Move to next step
    const nextStep = currentStep + 1;
    setCurrentStep(nextStep);
    setShowOptions(false);

    setTimeout(() => {
      displayMessage(nextStep);
    }, 500);
  };

  const currentScriptStep = CHATBOT_SCRIPT[currentStep];

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-blue-50 to-white min-h-[80vh]">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
            <MessageCircle size={32} className="text-blue-600" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: '#1e3a8a' }}>
            Mise en demeure, huissier, relance...
          </h1>
          <p className="text-gray-600 text-lg font-medium">Ne payez pas avant de vérifier</p>
          <p className="text-gray-500 text-sm mt-1">68% des dossiers analysés présentent des irrégularités</p>
        </div>

        {/* Trust badges */}
        <div className="flex flex-wrap justify-center gap-3 mb-6">
          <div className="flex items-center bg-green-50 border border-green-200 rounded-full px-3 py-1.5 text-sm">
            <Shield size={14} className="text-green-600 mr-1.5" />
            <span className="text-green-800 font-medium">Anonyme</span>
          </div>
          <div className="flex items-center bg-blue-50 border border-blue-200 rounded-full px-3 py-1.5 text-sm">
            <Lock size={14} className="text-blue-600 mr-1.5" />
            <span className="text-blue-800 font-medium">Confidentiel</span>
          </div>
          <div className="flex items-center bg-purple-50 border border-purple-200 rounded-full px-3 py-1.5 text-sm">
            <Clock size={14} className="text-purple-600 mr-1.5" />
            <span className="text-purple-800 font-medium">3 min</span>
          </div>
        </div>

        {/* Chat container */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          {/* Messages */}
          <div className="p-4 sm:p-6 space-y-4 max-h-[50vh] overflow-y-auto">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                    message.type === 'user'
                      ? 'bg-blue-600 text-white rounded-br-md'
                      : 'bg-gray-100 text-gray-800 rounded-bl-md'
                  }`}
                >
                  <p className="whitespace-pre-line text-sm sm:text-base">{message.content}</p>
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-3">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Options */}
          {showOptions && currentScriptStep && (
            <div className="border-t border-gray-100 p-4 sm:p-6 bg-gray-50">
              <div className="space-y-2">
                {currentScriptStep.buttons ? (
                  // Simple continue buttons
                  currentScriptStep.buttons.map((button, index) => (
                    <Button
                      key={index}
                      onClick={() => handleOptionClick(currentScriptStep, button)}
                      className="w-full py-3 text-base font-medium"
                      style={{ backgroundColor: '#1e3a8a', color: 'white' }}
                    >
                      {button.label}
                      <ArrowRight className="ml-2" size={18} />
                    </Button>
                  ))
                ) : (
                  // Question options
                  <div className="grid gap-2">
                    {currentScriptStep.options?.map((option, index) => (
                      <Button
                        key={index}
                        variant="outline"
                        onClick={() => handleOptionClick(currentScriptStep, option)}
                        className="w-full py-3 text-base font-medium justify-start text-left hover:bg-blue-50 hover:border-blue-300"
                      >
                        {option.label}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Progress indicator */}
        <div className="mt-6">
          <div className="flex justify-between text-sm text-gray-500 mb-2">
            <span>Progression</span>
            <span>{Math.round(((currentStep + 1) / CHATBOT_SCRIPT.length) * 100)}%</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-600 transition-all duration-500"
              style={{ width: `${((currentStep + 1) / CHATBOT_SCRIPT.length) * 100}%` }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ChatBotVariantC;
